import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import {
  useTanstackMenuFetchFn,
  useTanstackUserProfileFetchFn,
} from '@/components/Functional/hooks-core/useTanstackFetchFn'
import { useLogout } from '@/components/Functional/hooks/useLogout'
import type { MenuData, SnsAction } from '@/schemas/v2'
import { useMenuStore } from '@/store/useMenuStore'
import { useTokenStore } from '@/store/useTokenStore'
import { Overlay, Portal, Transition } from '@mantine/core'
import { IconBrandFacebook, IconBrandInstagram, IconBrandLine, IconBrandTiktok, IconBrandX } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Fragment, type ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './MenuDrawer.module.css'

/** http: または https: スキームのみを許可する */
const isSafeUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * SNS action → アイコンコンポーネントのマップ。
 * Record<SnsAction, ...> により、SnsActionSchema に種別を追加した際に
 * コンパイルエラーで対応漏れを検知できる。
 */
const SNS_ICON_MAP: Record<SnsAction, ReactElement> = {
  facebook: <IconBrandFacebook size={32} stroke={1.5} />,
  instagram: <IconBrandInstagram size={32} stroke={1.5} />,
  x: <IconBrandX size={32} stroke={1.5} />,
  line: <IconBrandLine size={32} stroke={1.5} />,
  tiktok: <IconBrandTiktok size={32} stroke={1.5} />,
}

export function MenuDrawer() {
  const { t } = useTranslation()
  const { isOpen, close } = useMenuStore()
  const { gen } = useCampaignSlug()
  const { logout } = useLogout()
  const router = useRouter()

  // 認証状態を取得（tokenLoading === true の間は isAuthenticated = false として扱い、チラつきを防ぐ）
  const idToken = useTokenStore((s) => s.idToken)
  const tokenLoading = useTokenStore((s) => s.loading)
  const isAuthenticated = !tokenLoading && idToken !== null

  const menuQuery = useTanstackMenuFetchFn()
  const { data: menuData } = useQuery({ ...menuQuery })

  const userProfileQuery = useTanstackUserProfileFetchFn()
  const { data: userProfile } = useQuery({ ...userProfileQuery, enabled: isAuthenticated })

  const handleLogout = async () => {
    await logout()
    router.reload()
  }

  /** メニュー項目を SNS とそれ以外に分離 */
  const { menuItems, snsItems } = useMemo(() => {
    const menus = menuData?.menus ?? []
    return {
      menuItems: menus.filter((item): item is Extract<MenuData, { type: 'URL' | 'STATIC' }> => item.type !== 'SNS'),
      snsItems: menus.filter((item): item is Extract<MenuData, { type: 'SNS' }> => item.type === 'SNS'),
    }
  }, [menuData])

  /** STATIC action に対応する URL を返す */
  const staticActionUrl = (action: string): string | null => {
    const docsUrl = process.env.NEXT_PUBLIC_DOCS_URL
    if (!docsUrl) return null

    switch (action) {
      case 'FAQ':
        return `${docsUrl}/faq`
      case 'TERMS':
        return `${docsUrl}/terms`
      case 'PRIVACY_POLICY':
        return `${docsUrl}/privacy`
      default:
        return null
    }
  }

  const itemClass = (idx: number) => `block text-h1 font-bold text-sub ${idx >= 1 ? 'mt-5' : ''}`

  const renderMenuItem = (item: MenuData, idx: number) => {
    const cls = itemClass(idx)

    switch (item.type) {
      case 'URL':
        if (!item.url || !isSafeUrl(item.url)) {
          return null
        }
        return (
          <a href={item.url} className={cls} target="_blank" rel="noreferrer noopener">
            {item.name}
          </a>
        )

      case 'STATIC': {
        const url = staticActionUrl(item.action)
        if (url) {
          return (
            <a href={url} className={cls} rel="noreferrer noopener">
              {item.name}
            </a>
          )
        }
        // RESET / RESTORE / LOGOUT はログイン時のみ表示
        // NOTE: tokenLoading === true の間も isAuthenticated === false となるため、
        // 認証確定前に項目が一瞬表示されるちらつきは発生しない。現状この実装で許容する。
        if (!isAuthenticated) return null
        if (item.action === 'RESET') {
          return (
            <button
              type="button"
              onClick={() => {
                location.href = gen('/reset')
              }}
              className={cls}
            >
              {item.name || t('common.reset', '獲得スタンプをリセットする')}
            </button>
          )
        }
        if (item.action === 'RESTORE') {
          return (
            <Link href={{ pathname: gen('/restore') }} className={cls}>
              {item.name || t('common.restore', 'データ復元')}
            </Link>
          )
        }
        if (item.action === 'LOGOUT') {
          return (
            <button type="button" className={cls} onClick={handleLogout}>
              {item.name || t('common.logout', 'ログアウト')}
            </button>
          )
        }
        return null
      }

      default:
        return null
    }
  }

  return (
    <Portal>
      {/* 背景オーバーレイ：ヘッダー直下から表示 */}
      <Transition mounted={isOpen} transition="fade" duration={200}>
        {(transitionStyles) => <Overlay onClick={close} zIndex={999} style={{ ...transitionStyles, top: '44px' }} />}
      </Transition>

      {/* ドロワー本体：左からスライドイン、ヘッダー直下から全画面 */}
      <Transition mounted={isOpen} transition="slide-right" duration={300} timingFunction="ease">
        {(transitionStyles) => (
          <div className={styles.drawer} style={transitionStyles}>
            <div className={styles.contents}>
              {menuItems.map((item, idx) => (
                <Fragment key={`menu-${item.action ?? item.url ?? idx}`}>{renderMenuItem(item, idx)}</Fragment>
              ))}
            </div>

            {/* SNS アイコン：右下に横並び */}
            {snsItems.length > 0 && (
              <div className="absolute bottom-16 right-4 flex items-center gap-3">
                {snsItems.map((item, idx) => {
                  const icon = SNS_ICON_MAP[item.action]
                  if (!isSafeUrl(item.url)) return null
                  return (
                    <a
                      key={`sns-${item.action ?? item.url ?? idx}`}
                      href={item.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-black"
                      aria-label={item.action}
                    >
                      {icon}
                    </a>
                  )
                })}
              </div>
            )}

            {/* バージョン表示・ユーザーID */}
            <div className="absolute bottom-4 right-4 flex flex-col items-end gap-1">
              {isAuthenticated && userProfile?.user.id != null && (
                <p className="text-xs text-black">id: {userProfile.user.id}</p>
              )}
              <p className="text-xs text-black">v2.0.0</p>
            </div>
          </div>
        )}
      </Transition>
    </Portal>
  )
}
